import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { Modal, Button } from '@components/common';
import { ApiCheckUnlock, fetcher, isCheckUnlockStatusOk } from '@utils/data-fetching';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import type { StatusGatePayload } from '@hooks/useSwrApi/endpoints/resume-operations/useSwrResumeSetStatus';
import { ApplicantStatus } from 'src/types/company-job';

export interface StatusChangeGateModalProps {
  /** URL slug of the candidate being moved. */
  userSlug: string;
  /** Slug of the job the candidate is applying to (used for the unlock POST body). */
  jobSlug: string;
  /** Numeric job id (used for the unlock POST body). */
  jobId: number;
  /** Job title (unlock endpoint accepts / echoes it). */
  jobTitle: string;
  /** Target status id — Shortlist (21) or Interview (3). Drives the modal copy. */
  targetStatus: number;
  /** Gate payload from the applicant DTO (or a 402 fallback body). */
  gate: StatusGatePayload;
  /** Locale for the fetcher. */
  locale: Locale;
}

/**
 * @description Pre-action confirmation modal for gated status transitions
 * (Shortlist / Interview on freemium & hire+ jobs). Renders the plan's
 * `cv.status_gate.*` copy driven by the applicant's gate payload, and on
 * confirm runs the two-step chain: unlock the candidate (v2 check-unlock),
 * then set the status. Resolves the NiceModal promise with `true` when the
 * status change actually landed, `false` on cancel or any failure. Callers
 * refresh their applicant list / mutate SWR when they see `true`.
 */
export const StatusChangeGateModal = NiceModal.create((props: StatusChangeGateModalProps) => {
  const {
    userSlug,
    jobSlug,
    jobId,
    jobTitle,
    targetStatus,
    gate,
    locale,
  } = props;

  const modal = useModal();
  const { t } = useTranslation('common');
  const { statusGenericErrorToast, status200Toast } = useStaticToasts();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isInterview = targetStatus === ApplicantStatus.INTERVIEW;
  const titleKey = isInterview ? 'cv.status_gate.interview.title' : 'cv.status_gate.shortlist.title';
  const bodyKey = isInterview ? 'cv.status_gate.interview.body' : 'cv.status_gate.shortlist.body';

  const body = t(bodyKey)
    .replace('{price}', String(gate.unlockPrice))
    .replace('{currency}', gate.currency);

  const closeAsCancelled = useCallback(() => {
    modal.resolve(false);
    void modal.hide();
  }, [modal]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Step 1: unlock the candidate. Uses the v2 slug-addressed endpoint —
      // this is the same call the standard "Contact / Message / Download"
      // flow performs and it charges the recruiter's wallet.
      const unlockResponse = await fetcher<ApiCheckUnlock>(
        `/v2/resume-operation/${userSlug}/check-unlock/contact`,
        locale,
        {
          method: 'POST',
          payload: {
            jobId,
            jobSlug,
            title: jobTitle,
          },
          stringifyPayload: true,
        },
      );

      if (!isCheckUnlockStatusOk(unlockResponse)) {
        statusGenericErrorToast(t('cv.status_gate.unlock_required').replace('{price}', String(gate.unlockPrice)));
        modal.resolve(false);
        void modal.hide();
        return;
      }

      // Step 2: now that the applicant is unlocked, run the status change.
      // If the wallet reservation somehow fails between steps (unlikely
      // because unlock already committed the credits), the backend guard
      // will still refuse and we bubble a generic error back.
      const statusResponse = await fetcher<ApiCheckUnlock>(
        `/v2/resume-operation/${userSlug}/status/${targetStatus}`,
        locale,
        {
          method: 'POST',
          payload: {
            jobId,
            title: jobTitle,
          },
          stringifyPayload: true,
        },
      );

      if (!isCheckUnlockStatusOk(statusResponse)) {
        statusGenericErrorToast();
        modal.resolve(false);
        void modal.hide();
        return;
      }

      status200Toast(t('cv.status_gate.status_change_success') || 'Status updated.');
      modal.resolve(true);
      void modal.hide();
    } catch {
      statusGenericErrorToast();
      modal.resolve(false);
      void modal.hide();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    userSlug,
    locale,
    jobId,
    jobSlug,
    jobTitle,
    targetStatus,
    gate.unlockPrice,
    t,
    statusGenericErrorToast,
    status200Toast,
    modal,
  ]);

  return (
    <Modal isOpen={modal.visible} onClose={closeAsCancelled} size="sm" centered>
      <Modal.Header onClose={closeAsCancelled}>
        <h3 className="text-lg font-bold text-ink">{t(titleKey)}</h3>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-ink-medium">{body}</p>
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <Button
          type="button"
          color="light"
          styling="outline"
          onClick={closeAsCancelled}
          disabled={isSubmitting}
        >
          {t('cv.status_gate.cancel')}
        </Button>
        <Button
          type="button"
          color="secondary"
          onClick={handleConfirm}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {t('cv.status_gate.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

/**
 * @description Show the gate modal and wait for the recruiter's decision.
 * Returns a promise that resolves to `true` when the unlock + status
 * change succeeded, `false` on cancel or any failure. Callers should
 * refresh their applicant / list state when they see `true`.
 */
export const showStatusChangeGateModal = (props: StatusChangeGateModalProps): Promise<boolean> => (
  NiceModal.show(StatusChangeGateModal, props) as Promise<boolean>
);
